import React, { useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: #0056b3;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ResponseArea = styled.pre`
  background-color: #f8f9fa;
  padding: 1rem;
  border: 1px solid #eee;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  max-height: 300px;
  overflow-y: auto;
`;

const ErrorMessage = styled.p`
  color: red;
  margin-top: 1rem;
`;

const TestSmsPage: React.FC = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [dltEntityId, setDltEntityId] = useState('');
  const [dltTemplateId, setDltTemplateId] = useState('');
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setResponse(null);
    setError(null);
    axios.defaults.withCredentials = true; // If your API requires auth cookies

    try {
      const result = await axios.post(`${API_BASE}/api/test-sms`, {
        mobileNumber,
        messageContent,
        dltEntityId,
        dltTemplateId,
      });
      setResponse(result.data);
    } catch (err: any) {
      console.error('Error sending test SMS:', err);
      if (axios.isAxiosError(err) && err.response) {
        setError(`Server Error: ${err.response.status} - ${err.response.data?.message || err.message}`);
        setResponse(err.response.data);
      } else {
        setError(`Request Error: ${err.message}`);
      }
    }
    setIsLoading(false);
  };

  return (
    <PageContainer>
      <Title>Test NIC SMS Gateway</Title>
      <Form onSubmit={handleSubmit}>
        <div>
          <Label htmlFor="mobileNumber">Mobile Number (e.g., 91XXXXXXXXXX):</Label>
          <Input
            id="mobileNumber"
            type="text"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="messageContent">Message Content:</Label>
          <TextArea
            id="messageContent"
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="dltEntityId">DLT Entity ID:</Label>
          <Input
            id="dltEntityId"
            type="text"
            value={dltEntityId}
            onChange={(e) => setDltEntityId(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="dltTemplateId">DLT Template ID:</Label>
          <Input
            id="dltTemplateId"
            type="text"
            value={dltTemplateId}
            onChange={(e) => setDltTemplateId(e.target.value)}
            required
          />
        </div>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Sending...' : 'Send Test SMS'}
        </Button>
      </Form>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {response && (
        <>
          <h3>Response:</h3>
          <ResponseArea>{JSON.stringify(response, null, 2)}</ResponseArea>
        </>
      )}
    </PageContainer>
  );
};

export default TestSmsPage;
